import { Box, LinearProgress, linearProgressClasses, styled, Typography } from '@mui/material';
import { IconApproved, IconClose, IconDocument1, IconDownload, IconTrash } from 'assets/img';
import { formatFileSize } from './format-file-size';
import { FileDropzoneFileItemProps, FileDropzoneStatus } from './file-dropzone.types';
import { LwIconButton } from '../../icon-button';

export const FileDropzoneFileItem = ({
  item,
  labels,
  disabled,
  onRemoveFile,
  onCancelUpload,
}: FileDropzoneFileItemProps) => {
  const isUploaded = item.status === FileDropzoneStatus.Uploaded;

  const handleDownload = () => {
    const url = URL.createObjectURL(item.file);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = item.file.name;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleDismiss = () => {
    if (isUploaded) {
      onRemoveFile(item.file);
      return;
    }
    onCancelUpload?.(item.file);
  };

  return (
    <FileRow>
      <FileIconWrapper>
        <IconDocument1 width={24} height={24} />
      </FileIconWrapper>
      <FileMeta>
        <Typography variant="body1" noWrap>
          {item.file.name}
        </Typography>
        <Typography variant="body2">{formatFileSize(item.file.size)}</Typography>
      </FileMeta>
      {isUploaded ? (
        <UploadedStatus>
          <Typography variant="body2">{labels.uploaded}</Typography>
          <IconApproved width={20} height={20} />
        </UploadedStatus>
      ) : (
        <ProgressWrapper>
          <StyledLinearProgress variant="determinate" value={item.progress ?? 0} />
        </ProgressWrapper>
      )}
      <Actions>
        {isUploaded ? (
          <LwIconButton
            iconColorMode="stroke"
            color="secondary"
            size="small"
            disabled={disabled}
            onClick={handleDownload}
            aria-label={labels.download}
          >
            <IconDownload width={18} height={18} />
          </LwIconButton>
        ) : null}
        <LwIconButton
          iconColorMode={isUploaded ? 'stroke' : 'fill'}
          color="error"
          size="small"
          disabled={disabled}
          onClick={handleDismiss}
          aria-label={isUploaded ? labels.remove : labels.cancel}
        >
          {isUploaded ? <IconTrash width={18} height={18} /> : <IconClose width={18} height={18} />}
        </LwIconButton>
      </Actions>
    </FileRow>
  );
};

const FileRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(3, 0),
}));

const FileIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  color: theme.palette.lwPrimaryAccentDark[60],
  flexShrink: 0,
}));

const FileMeta = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
});

const UploadedStatus = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: theme.palette.lwSuccess[100],
  flexShrink: 0,
}));

const ProgressWrapper = styled(Box)({
  flex: 1,
  minWidth: 80,
});

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.lwPrimaryAccentLight[10],
  [`& .${linearProgressClasses.bar}`]: {
    backgroundColor: theme.palette.lwPrimaryAccentLight[100],
    borderRadius: theme.spacing(1),
  },
}));

const Actions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
}));
